/**
 * Context-aware PDP breadcrumbs and related discovery blocks.
 */
import { formatMoney, priceToMinorUnits } from '@theme/storefront-cart';
import { bindCarouselMouseDrag } from '@theme/carousel-touch-axis';

const STORAGE_KEY = 'rh_nav_collection';

/** @typedef {{ handle: string, title: string, url: string }} NavCollection */
/** @typedef {{ productId: number, productTitle: string, productUrl: string, vendor: string, vendorHandle: string, brandCollection: NavCollection, fromParam: string, defaultContextHandle: string, collections: NavCollection[], utilityHandles: string[], relatedLimit: number, moneyFormat?: string, currency?: string }} NavData */

/** @returns {NavData | null} */
function readNavData() {
  const el = document.getElementById('rh-product-nav-data');
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/** @param {string | null | undefined} handle @param {NavData} data */
function isValidContext(handle, data) {
  if (!handle) return false;
  return data.collections.some((col) => col.handle === handle);
}

/** @param {NavData} data */
function resolveContextHandle(data) {
  const params = new URLSearchParams(window.location.search);
  const fromParam = params.get('from');
  if (isValidContext(fromParam, data)) return fromParam;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (isValidContext(stored, data)) return stored;
  } catch {
    /* no-op */
  }

  const fromReferrer = parseCollectionFromReferrer();
  if (isValidContext(fromReferrer, data)) return fromReferrer;

  if (isValidContext(data.fromParam, data)) return data.fromParam;
  if (isValidContext(data.defaultContextHandle, data)) return data.defaultContextHandle;

  return data.collections[0]?.handle || null;
}

/** @returns {string | null} */
function parseCollectionFromReferrer() {
  try {
    const ref = new URL(document.referrer);
    if (ref.hostname !== window.location.hostname) return null;
    const match = ref.pathname.match(/^\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/** @param {string} handle @param {NavData} data @returns {NavCollection | undefined} */
function getCollection(handle, data) {
  return data.collections.find((col) => col.handle === handle);
}

/** @param {string} contextHandle @param {NavData} data */
function getSiblingCollections(contextHandle, data) {
  return data.collections.filter((col) => col.handle !== contextHandle);
}

/** @param {string} contextHandle @param {NavData} data */
function updateBreadcrumbs(contextHandle, data) {
  const nav = document.querySelector('[data-rh-breadcrumbs]');
  if (!nav) return;

  const contextCol = getCollection(contextHandle, data);
  const collectionCrumb = nav.querySelector('[data-rh-crumb-collection]');
  const collectionLink = collectionCrumb?.querySelector('a');
  const titleEl = nav.querySelector('[data-rh-crumb-product]');

  if (contextCol && collectionLink && collectionCrumb) {
    collectionLink.href = contextCol.url;
    collectionLink.textContent = contextCol.title;
    collectionCrumb.hidden = false;
  } else if (collectionCrumb) {
    collectionCrumb.hidden = true;
  }

  if (titleEl) {
    titleEl.textContent = data.productTitle.length > 48 ? `${data.productTitle.slice(0, 48)}…` : data.productTitle;
  }

  updateBreadcrumbJsonLd(contextHandle, data, contextCol);
}

/** @param {string} contextHandle @param {NavData} data @param {NavCollection | undefined} contextCol */
function updateBreadcrumbJsonLd(contextHandle, data, contextCol) {
  const script = document.getElementById('rh-breadcrumb-jsonld');
  if (!script || !contextCol) return;

  const homeUrl = `${window.location.origin}/`;
  const payload = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: homeUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: contextCol.title,
        item: window.location.origin + contextCol.url,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: data.productTitle,
        item: window.location.origin + data.productUrl,
      },
    ],
  };

  script.textContent = JSON.stringify(payload);
}

const QUICK_LINK_ICONS = {
  collection:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>',
  brand:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>',
  category:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>',
};

/** @param {{ href: string, label: string, type: 'collection' | 'brand' | 'category' }} item */
function quickLinkChipHtml(item) {
  return `
    <li>
      <a class="rh-product-context__chip rh-product-context__chip--${item.type}" href="${escapeAttr(item.href)}">
        <span class="rh-product-context__chip-icon" aria-hidden="true">${QUICK_LINK_ICONS[item.type]}</span>
        <span class="rh-product-context__chip-label">${escapeHtml(item.label)}</span>
      </a>
    </li>
  `;
}

/** @param {string} contextHandle @param {NavData} data */
function renderQuickLinks(contextHandle, data) {
  const root = document.querySelector('[data-rh-product-quick-links]');
  if (!root) return;

  const contextCol = getCollection(contextHandle, data);
  const siblings = getSiblingCollections(contextHandle, data);
  /** @type {Array<{ href: string, label: string, type: 'collection' | 'brand' | 'category' }>} */
  const items = [];

  if (contextCol) {
    items.push({ href: contextCol.url, label: `More in ${contextCol.title}`, type: 'collection' });
  }

  if (data.vendor && data.brandCollection?.url) {
    items.push({ href: data.brandCollection.url, label: `All ${data.vendor}`, type: 'brand' });
  }

  siblings.forEach((col) => {
    items.push({ href: col.url, label: col.title, type: 'category' });
  });

  if (items.length === 0) {
    root.hidden = true;
    return;
  }

  root.hidden = false;
  root.innerHTML = `
    <ul class="rh-product-context__list">
      ${items.map((item) => quickLinkChipHtml(item)).join('')}
    </ul>
  `;
}

/** @param {string} str */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** @param {string} str */
function escapeAttr(str) {
  return escapeHtml(str);
}

/** @param {string | null | undefined} src @param {number} width */
function imageWithWidth(src, width) {
  if (!src) return null;
  let url = String(src);
  if (url.startsWith('//')) url = `https:${url}`;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}width=${width}`;
}

/** @param {object} product */
function resolveProductImage(product) {
  const imgs = product.images || [];
  const fromImages = imgs.map((img) => (typeof img === 'string' ? img : img?.src)).find(Boolean);
  const featured =
    product.featured_image ||
    product.image ||
    fromImages ||
    product.variants?.find((v) => v?.featured_image?.src)?.featured_image?.src ||
    null;

  return imageWithWidth(featured, 360);
}

/** @param {object} product */
function normalizeJsonProduct(product) {
  const variant =
    product.variants?.find((v) => v.available !== false && v.available !== 'false') ||
    product.variants?.[0];

  const priceMinor = priceToMinorUnits(variant?.price ?? product.price ?? 0);
  const compareMinor = variant?.compare_at_price ? priceToMinorUnits(variant.compare_at_price) : 0;
  const handle = product.handle || String(product.url || '').replace(/^.*\/products\//, '');

  return {
    id: product.id,
    title: product.title || '',
    url: product.url || `/products/${handle}`,
    image: resolveProductImage(product),
    priceMinor,
    compareMinor: compareMinor > priceMinor ? compareMinor : 0,
  };
}

/** @param {string} productUrl @param {string | null} fromHandle */
function productUrlWithFrom(productUrl, fromHandle) {
  if (!fromHandle) return productUrl;
  try {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('from', fromHandle);
    return url.pathname + url.search;
  } catch {
    return productUrl;
  }
}

/** @param {{ title: string, url: string, image: string | null, priceMinor: number, compareMinor: number }} item @param {string | null} fromHandle */
function productCardHtml(item, fromHandle) {
  const href = productUrlWithFrom(item.url, fromHandle);
  const priceHtml =
    item.compareMinor > 0
      ? `<span class="rh-product-family__price">${formatMoney(item.priceMinor)} <s class="rh-product-family__compare">${formatMoney(item.compareMinor)}</s></span>`
      : `<span class="rh-product-family__price">${formatMoney(item.priceMinor)}</span>`;

  return `
    <li class="rh-product-family__slide">
      <a class="rh-product-family__card" href="${escapeAttr(href)}">
        <span class="rh-product-family__media">
          ${
            item.image
              ? `<img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.title)}" loading="lazy" width="360" height="360">`
              : '<span class="rh-product-family__placeholder"></span>'
          }
        </span>
        <span class="rh-product-family__meta">
          <span class="rh-product-family__name">${escapeHtml(item.title)}</span>
          ${priceHtml}
        </span>
      </a>
    </li>
  `;
}

/** @param {string} id @param {string} label @param {string} cardsHtml */
function carouselSectionHtml(id, label, cardsHtml) {
  return `
    <div class="rh-product-family__track-wrap">
      <button type="button" class="rh-product-family__arrow rh-product-family__arrow--prev" data-rh-carousel-prev aria-label="Scroll ${escapeAttr(label)} left">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12 4l-6 6 6 6" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <ul class="rh-product-family__track" id="${escapeAttr(id)}" data-rh-product-track tabindex="0">${cardsHtml}</ul>
      <button type="button" class="rh-product-family__arrow rh-product-family__arrow--next" data-rh-carousel-next aria-label="Scroll ${escapeAttr(label)} right">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M8 4l6 6-6 6" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  `;
}

/** @param {ParentNode} root */
function initCarousels(root) {
  root.querySelectorAll('[data-rh-product-track]').forEach((track) => {
    if (!(track instanceof HTMLElement)) return;

    const wrap = track.closest('.rh-product-family__track-wrap');
    const prev = wrap?.querySelector('[data-rh-carousel-prev]');
    const next = wrap?.querySelector('[data-rh-carousel-next]');

    prev?.addEventListener('click', () => {
      track.scrollBy({ left: -Math.max(track.clientWidth * 0.72, 220), behavior: 'smooth' });
    });
    next?.addEventListener('click', () => {
      track.scrollBy({ left: Math.max(track.clientWidth * 0.72, 220), behavior: 'smooth' });
    });

    bindCarouselMouseDrag(track);
  });
}

/** @param {string} handle @param {number} limit */
async function fetchCollectionProducts(handle, limit) {
  const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=${Math.min(limit, 20)}`);
  if (!response.ok) return [];
  const json = await response.json();
  return Array.isArray(json.products) ? json.products : [];
}

/** @param {string} contextHandle @param {NavData} data */
async function renderRelatedBlocks(contextHandle, data) {
  const root = document.querySelector('[data-rh-product-related-root]');
  if (!root) return;

  const contextCol = getCollection(contextHandle, data);
  const siblings = getSiblingCollections(contextHandle, data);
  const fetchLimit = 12;
  const displayLimit = data.relatedLimit || 8;
  const blocks = [];

  if (contextCol) {
    try {
      const products = (await fetchCollectionProducts(contextCol.handle, fetchLimit))
        .filter((p) => p.id !== data.productId)
        .slice(0, displayLimit)
        .map(normalizeJsonProduct);

      if (products.length > 0) {
        const cards = products.map((p) => productCardHtml(p, contextCol.handle)).join('');
        blocks.push(`
          <section class="rh-product-family" aria-label="More products in ${escapeHtml(contextCol.title)}">
            <div class="rh-product-family__head">
              <div class="rh-product-family__head-copy">
                <p class="rh-product-family__eyebrow">From this collection</p>
                <h3 class="rh-product-family__heading">More in ${escapeHtml(contextCol.title)}</h3>
              </div>
              <a class="rh-product-family__head-link" href="${escapeAttr(contextCol.url)}">View all</a>
            </div>
            ${carouselSectionHtml(`rh-carousel-collection-${contextCol.handle}`, contextCol.title, cards)}
          </section>
        `);
      }
    } catch {
      /* no-op */
    }
  }

  if (data.brandCollection?.url && data.vendor) {
    try {
      const brandProducts = (await fetchCollectionProducts(data.brandCollection.handle, fetchLimit))
        .filter((p) => p.id !== data.productId)
        .slice(0, displayLimit)
        .map(normalizeJsonProduct);

      if (brandProducts.length > 0) {
        const cards = brandProducts.map((p) => productCardHtml(p, data.brandCollection.handle)).join('');
        blocks.push(`
          <section class="rh-product-family" aria-label="More ${escapeHtml(data.vendor)} products">
            <div class="rh-product-family__head">
              <div class="rh-product-family__head-copy">
                <p class="rh-product-family__eyebrow">Same brand</p>
                <h3 class="rh-product-family__heading">More ${escapeHtml(data.vendor)} products</h3>
              </div>
              <a class="rh-product-family__head-link" href="${escapeAttr(data.brandCollection.url)}">View all</a>
            </div>
            ${carouselSectionHtml(`rh-carousel-brand-${data.brandCollection.handle}`, data.vendor, cards)}
          </section>
        `);
      }
    } catch {
      /* no-op */
    }
  }

  if (siblings.length > 0) {
    blocks.push(`
      <section class="rh-product-discovery__similar" aria-label="Similar collections">
        <div class="rh-product-family__head">
          <div class="rh-product-family__head-copy">
            <p class="rh-product-family__eyebrow">Explore more</p>
            <h3 class="rh-product-family__heading">Similar collections</h3>
          </div>
        </div>
        <ul class="rh-collection-links__list">
          ${siblings.map((col) => `<li><a class="rh-collection-links__chip" href="${escapeAttr(col.url)}">${escapeHtml(col.title)}</a></li>`).join('')}
        </ul>
      </section>
    `);
  }

  root.innerHTML = blocks.join('');
  initCarousels(root);

  const section = root.closest('.ppdp-related-discovery');
  if (section) {
    section.hidden = blocks.length === 0;
  }
}

function init() {
  const data = readNavData();
  if (!data) return;

  const contextHandle = resolveContextHandle(data);
  if (!contextHandle) return;

  try {
    sessionStorage.setItem(STORAGE_KEY, contextHandle);
  } catch {
    /* no-op */
  }

  updateBreadcrumbs(contextHandle, data);
  renderQuickLinks(contextHandle, data);
  renderRelatedBlocks(contextHandle, data);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
